import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  showDetails: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, showDetails: false }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, showDetails: false })
  }

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }))
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const { error, showDetails } = this.state

      return (
        <div className="flex items-center justify-center min-h-[300px] p-6">
          <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-surface-1 p-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-danger/10 mb-4">
                <AlertTriangle className="w-7 h-7 text-danger" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-100 mb-1">
                Something went wrong
              </h3>
              <p className="text-sm text-zinc-400 mb-5">
                An unexpected error occurred. You can try again or contact support
                if the problem persists.
              </p>

              <button
                onClick={this.handleReset}
                className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
              >
                <RotateCcw className="w-4 h-4" />
                Try Again
              </button>
            </div>

            {error && (
              <div className="mt-5 border-t border-zinc-800 pt-4">
                <button
                  onClick={this.toggleDetails}
                  className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-400 transition-colors"
                >
                  {showDetails ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                  {showDetails ? 'Hide details' : 'Show details'}
                </button>
                {showDetails && (
                  <pre
                    className={cn(
                      'mt-2 max-h-40 overflow-auto rounded-lg bg-zinc-900 p-3',
                      'text-xs text-zinc-400 font-mono whitespace-pre-wrap break-words'
                    )}
                  >
                    {error.message}
                    {error.stack && `\n\n${error.stack}`}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
